import { Question } from 'src/app/core/models/questionnaire.model';

const SIMPLE_QUESTIONNAIRE_SESSION_KEY = 'simpleQuestionnaireSession';

/**
 * CPK-4743: set by the one-time-link entry point when the URL carries
 * simpleQuestionnaire=true. It lives in localStorage, so it outlives the tab and is
 * shared by every tab of the same origin — what ends it is the next one-time-link
 * entry, which always clears it before deciding whether to set it again. A regular
 * agent impersonation therefore resets it, even in the same browser.
 *
 * Not a security boundary: anyone can set a localStorage key, so this only shapes the
 * UI (skippable questions, "continue later"). What the session may actually write is
 * re-checked server-side — defer-case-action requires the AGENT role, the case opt-in
 * and the claim-scoped use_simple_questionnaire permission.
 */
export function isSimpleQuestionnaireSessionActive(): boolean {
  return localStorage.getItem(SIMPLE_QUESTIONNAIRE_SESSION_KEY) === 'true';
}

const SKIPPED_QUESTIONNAIRES_KEY = 'simpleQuestionnaireSkippedQuestionnaires';

function readSkippedQuestionnaires(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(SKIPPED_QUESTIONNAIRES_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * CPK-4878: an agent who finishes a questionnaire with can_skip_question questions left
 * empty saves it OPEN — for the client those questions are still required. The guide
 * then treats it as done only for the rest of this Simple Questionnaire session, so the
 * agent can reach the next actions. Never persisted server-side (unlike deferred_claims,
 * which would count as done for the client too); cleared with the session flag on the
 * next one-time-link entry.
 */
export function markQuestionnaireSkippedInSession(claimId: string, questionnaireId: string): void {
  const entry = `${claimId}:${questionnaireId}`;
  const skipped = readSkippedQuestionnaires();
  if (skipped.includes(entry)) return;
  localStorage.setItem(SKIPPED_QUESTIONNAIRES_KEY, JSON.stringify([...skipped, entry]));
}

export function isQuestionnaireSkippedInSession(claimId: string, questionnaireId: string): boolean {
  if (!isSimpleQuestionnaireSessionActive()) return false;
  return readSkippedQuestionnaires().includes(`${claimId}:${questionnaireId}`);
}

export function clearQuestionnairesSkippedInSession(): void {
  localStorage.removeItem(SKIPPED_QUESTIONNAIRES_KEY);
}

/**
 * CPK-4846: Simple Questionnaire eligibility lives on the case
 * (cp_case.configuration.simple_questionnaire), not on the questionnaire — the same
 * cp_questionnaire can be shared by several cases. Anything other than a literal
 * `true` (missing config, lookup failure) means not eligible.
 */
export function caseAllowsSimpleQuestionnaire(caseConfiguration: unknown): boolean {
  return (
    (caseConfiguration as { simple_questionnaire?: unknown } | null | undefined)
      ?.simple_questionnaire === true
  );
}

/**
 * CPK-4742: a question configured with can_skip_question only becomes skippable when
 * the case opted in (CPK-4846: cp_case.configuration.simple_questionnaire) AND the client
 * is answering through an active Simple Questionnaire session. Outside that combination,
 * `elective` behaves exactly as persisted.
 */
export function isQuestionSkippableInSimpleQuestionnaire(
  question: Pick<Question, 'configuration'> | undefined,
  caseAllowsSkip: boolean
): boolean {
  return (
    isSimpleQuestionnaireSessionActive() &&
    caseAllowsSkip &&
    question?.configuration?.simple_questionnaire?.can_skip_question === true
  );
}

/** The runtime-effective elective value — never persisted back, only used to drive rendering/validation. */
export function isEffectivelyElective(
  question: Pick<Question, 'elective' | 'configuration'> | undefined,
  caseAllowsSkip: boolean
): boolean {
  return (
    question?.elective === true ||
    isQuestionSkippableInSimpleQuestionnaire(question, caseAllowsSkip)
  );
}
